import { replayGamePath, replayHubPath } from '../replay/replayUrl';

const EXPLORER_BASE_PATH = '/explorer';

export type ExplorerBoardOrientation = 'white' | 'black';

/** Parse board orientation from `/explorer?orientation=black`. */
export function parseExplorerBoardOrientation(
  search: string,
): ExplorerBoardOrientation | undefined {
  const value = new URLSearchParams(search).get('orientation');
  if (value === 'black' || value === 'white') {
    return value;
  }
  return undefined;
}

function explorerPathWithOrientation(
  path: string,
  orientation?: ExplorerBoardOrientation,
): string {
  if (orientation !== 'black') {
    return path;
  }
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}orientation=black`;
}

/** Legacy `/explorer?replay=…` → `/replay` or `/replay/:gameId`. */
export function legacyExplorerReplayRedirect(search: string): string | null {
  const params = new URLSearchParams(search);
  if (!params.has('replay')) {
    return null;
  }

  const rawGameId = params.get('replay')?.trim();
  const legacyStartFen = params.get('startFen');
  const plyParam = params.get('ply');

  if (rawGameId) {
    if (plyParam) {
      const parsedPly = Number.parseInt(plyParam, 10);
      const halfMove =
        Number.isFinite(parsedPly) && parsedPly >= 1 ? parsedPly : undefined;
      return replayGamePath(rawGameId, halfMove);
    }
    if (legacyStartFen) {
      return `${replayGamePath(rawGameId)}?startFen=${encodeURIComponent(legacyStartFen)}`;
    }
    return replayGamePath(rawGameId);
  }
  return replayHubPath();
}

/** Parse SAN moves from `/explorer?moves=d4,d5,c4`. */
export function parseExplorerMoves(search: string): string[] {
  const movesParam = new URLSearchParams(search).get('moves');
  if (!movesParam) {
    return [];
  }

  return movesParam
    .split(',')
    .map((move) => decodeURIComponent(move.trim()))
    .filter(Boolean);
}

/** Build `/explorer` or `/explorer?moves=d4,d5,c4`. */
export function explorerPathForMoves(
  moves: string[],
  options?: { orientation?: ExplorerBoardOrientation },
): string {
  const base =
    moves.length === 0
      ? EXPLORER_BASE_PATH
      : `${EXPLORER_BASE_PATH}?moves=${moves.map(encodeURIComponent).join(',')}`;
  return explorerPathWithOrientation(base, options?.orientation);
}

/** Deep-link to the explorer position after {@code plyIndex} SAN moves from the start. */
export function explorerPathForMovesUpToPly(
  movesSan: readonly string[],
  plyIndex: number,
  options?: { orientation?: ExplorerBoardOrientation },
): string {
  const clamped = Math.max(0, Math.min(plyIndex, movesSan.length));
  return explorerPathForMoves(movesSan.slice(0, clamped), options);
}

export function explorerUrl(pathname: string, search: string): string {
  return `${pathname}${search}`;
}

/** Root explorer browse view (starting position). */
export function explorerRootPath(): string {
  return EXPLORER_BASE_PATH;
}

/** True when the explorer URL line was cleared (e.g. top-nav reset to starting position). */
export function explorerLineWasCleared(
  previousMoves: string[],
  nextMoves: string[],
): boolean {
  return previousMoves.length > 0 && nextMoves.length === 0;
}

/**
 * Explorer path to navigate to after an internal line change, or null when the
 * URL is already in sync or the callback is stale during an external URL reset.
 */
export function explorerPathForLineSansUpdate(
  urlMoves: string[],
  lineSans: string[],
  options?: { orientation?: ExplorerBoardOrientation },
): string | null {
  const urlKey = urlMoves.join('|');
  const lineKey = lineSans.join('|');

  if (lineKey !== urlKey) {
    const lineIsPrefixOfUrl =
      urlMoves.length >= lineSans.length &&
      lineSans.every((move, index) => urlMoves[index] === move);
    const urlIsPrefixOfLine =
      lineSans.length >= urlMoves.length &&
      urlMoves.every((move, index) => lineSans[index] === move);
    if (!lineIsPrefixOfUrl && !urlIsPrefixOfLine) {
      return null;
    }
  }

  const nextPath = explorerPathForMoves(lineSans, options);
  const currentPath = explorerPathForMoves(urlMoves, options);
  if (nextPath === currentPath) {
    return null;
  }
  return nextPath;
}
